import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbItem } from "abreact/misc/AbItem";
import { AbSize, AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { AbButtonSize as AbButtonSizeAbSDK } from "absdk/commons/ui/misc/AbButton";
import { AbButtonSize as AbButtonSizePublic } from "abreact/public/core/AbButton";
import { action } from "mobx";
import * as React from "react";
import * as AbHooks from "abreact/utils/AbHooks";
import { AbToolbarContext } from "abreact/layout/toolbar/AbToolbar";
export let AbButtonType = /*#__PURE__*/function (AbButtonType) {
  AbButtonType["DEFAULT"] = "";
  /* Make sure this stays as the empty string. Icon color reversal depends (ABSDK-575) on it */
  AbButtonType["PRIMARY"] = "ab-button-primary";
  AbButtonType["SUBMIT"] = "ab-button-submit";
  AbButtonType["SUCCESS"] = "ab-button-success";
  AbButtonType["INFO"] = "ab-button-info";
  AbButtonType["WARNING"] = "ab-button-warning";
  AbButtonType["DANGER"] = "ab-button-danger";
  AbButtonType["LINK"] = "ab-button-link";
  return AbButtonType;
}({});
export let AbButtonIconSide = /*#__PURE__*/function (AbButtonIconSide) {
  AbButtonIconSide["LEFT"] = "left";
  AbButtonIconSide["RIGHT"] = "right";
  return AbButtonIconSide;
}({});

/**
 * Properties of the AbButton component.
 */

const renderImage = props => {
  // Always render an icon when we are busy
  const icon = props.busy ? "absdk:wait" : props.icon;
  if (!icon) return null;
  if (typeof icon !== "string") return icon;
  return /*#__PURE__*/React.createElement(AbIcon, {
    image: icon,
    className: "ab-button-icon"
  });
};

/**
 * The **AbButton** component is a basic button that performs an action when clicked.
 */
export const AbButton = props => {
  const uniqueId = AbHooks.useUniqueId("ab-button");
  const contextSize = React.useContext(AbSizeContext);
  const isLinkButton = props.type === AbButtonType.LINK;
  const inToolbar = React.useContext(AbToolbarContext);
  const [cachedPadding, setCachedPadding] = React.useState(undefined);
  const buttonRef = AbHooks.useResizeObserver((_, observed) => {
    if ((props.__squishyPadding || observed.scrollWidth > observed.offsetWidth) && !cachedPadding) {
      const styling = window.getComputedStyle(observed);
      setCachedPadding({
        paddingLeft: styling.paddingLeft,
        paddingRight: styling.paddingRight
      });
    }
  });
  const iconOnly = Boolean(props.icon && !props.label);
  const isButtonSquare = Boolean(props.icon && !props.label && typeof props.icon === "string");
  const {
    id,
    ...buttonProps
  } = ReactUtils.inputProps("ab-button", {
    ...props,
    disabled: props.disabled || props.busy
  },
  // Make busy buttons disabled
  ["abui", props.type?.toString(), props.size ?? contextSize ?? (isLinkButton ? undefined : AbSize.MEDIUM), iconOnly && "btn-icon-only", props.busy && "ab-button-busy", isButtonSquare && "ab-square-button", props.iconSide === AbButtonIconSide.RIGHT ? "ab-button-side-right" : "ab-button-side-left", inToolbar && "ab-no-border-button"]);

  // if onClick defined wrap in a mobx action
  const onClick = props.onClick ? action(props.onClick) : undefined;

  // Note: there is a Chrome bug where disabled React buttons do not fire mouse leave events.
  // To work around this, we don't disable buttons with tooltips explicitly, but instead remove the
  // focus and onClick handling, and visually display the button as disabled.
  //
  // One downside to this approach is that for screen readers and other accessibility tools,
  // the button does not appear to be disabled. This may be something we have to address
  // in the future.
  //
  // See: https://jira.abinitio.com/browse/CAT-1270 and https://github.com/facebook/react/issues/4251
  const useFakeDisabling = buttonProps.disabled && props.tooltip;
  const inner = /*#__PURE__*/React.createElement(AbWithTooltip, _extends({
    clippedTrigger: props.tooltipProps?.clippedTrigger ?? buttonRef,
    tooltipOpenDelay: props.tooltipProps?.tooltipOpenDelay
  }, AbWithTooltip.makeClippedProps(props.tooltip,
  // Only show clipped tooltip if it's in the props or there is a label. Icon-only clipped tooltip is too weird.
  props.tooltipProps?.clippedTooltip ?? (props.label ? /*#__PURE__*/React.createElement(AbItem, {
    label: props.label,
    icon: props.icon
  }) : undefined))), /*#__PURE__*/React.createElement("button", _extends({}, buttonProps, props.eventHandlers, {
    style: {
      ...buttonProps.style,
      ...(cachedPadding ? {
        paddingLeft: 0,
        paddingRight: 0,
        justifyContent: "flex-start"
      } : {})
    },
    id: id ?? uniqueId,
    type: props.type === AbButtonType.SUBMIT ? "submit" : "button",
    disabled: useFakeDisabling ? false : buttonProps.disabled,
    tabIndex: useFakeDisabling ? -1 : buttonProps.tabIndex,
    onClick: buttonProps.disabled ? undefined : onClick,
    "data-test-component": "ab-button",
    "data-test-id": props.dataTestId,
    autoFocus: props.autoFocus,
    name: props.name
  }, props.__htmlProps, {
    ref: ReactUtils.mergeRefs(props.abRef, buttonRef)
  }), cachedPadding && /*#__PURE__*/React.createElement("div", {
    className: "ab-button-spacer",
    style: {
      width: cachedPadding?.paddingLeft
    }
  }), props.iconSide !== AbButtonIconSide.RIGHT && renderImage(props), props.label && props.label, props.iconSide === AbButtonIconSide.RIGHT && renderImage(props), cachedPadding && /*#__PURE__*/React.createElement("div", {
    className: "ab-button-spacer",
    style: {
      width: cachedPadding?.paddingRight
    }
  })));
  return sizeProviderWrapper(inner, props, contextSize);
};
const mergeChildrenWithLabel = (label, children) => {
  if (label && !children) {
    return label;
  }
  if (children && !label) {
    return children;
  }
  if (label && children) {
    if (typeof label === "string" && typeof children === "string") {
      return `${label} ${children}`;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, label, children);
  }
  return undefined;
};
export const mergeButtonSizeTypes = size => {
  switch (size) {
    case AbButtonSizeAbSDK.EXTRA_SMALL:
    case AbButtonSizePublic.EXTRASMALL:
      return AbSize.EXTRA_SMALL;
    case AbButtonSizeAbSDK.SMALL:
    case AbButtonSizePublic.SMALL:
      return AbSize.SMALL;
    case AbButtonSizeAbSDK.DEFAULT:
    case AbButtonSizePublic.DEFAULT:
      return AbSize.MEDIUM;
    case AbButtonSizeAbSDK.LARGE:
    case AbButtonSizePublic.LARGE:
      return AbSize.LARGE;
  }
  return size;
};
const autoCustomSize = (propSize, icon, children) => {
  if (propSize !== undefined) {
    return propSize;
  }
  if (typeof (icon ?? "") !== "string" || typeof (children ?? "") !== "string") {
    return AbSize.CUSTOM;
  }
  return undefined;
};
export function convertButtonProps(props) {
  if (props === undefined) {
    return undefined;
  }
  const {
    label,
    children,
    size,
    ...rest
  } = props;
  const mergedLabel = mergeChildrenWithLabel(label, children);
  return {
    ...rest,
    ...(mergedLabel && {
      label: mergedLabel
    }),
    size: autoCustomSize(mergeButtonSizeTypes(size), props.icon, children)
  };
}
//# sourceMappingURL=AbButton.js.map
